// TODO: Migration ot translatedCopy.js of raw strings
// TODO: Using generative voice service to enable generative at mount

import React, { useState, useEffect, useRef } from 'react';
import { getUserData } from '../../services/API';
import { sanitizeDataForSentry } from '../../services/util';
import DialogDisplay from '../DialogDisplay/DialogDisplay';
import VideoPlayer from '../VideoPlayer/VideoPlayer';
import RetellSubmitButton from './RetellSubmitButton';
import { useContainerHeightVar } from '../Formative/containerHeight';
import { COPY_STRINGS } from '../../constants/translatedCopy';
import { getCopyByGradeband } from '../../services/ThemingService';
import { logSessionInfo } from '../../services/logger';
import * as Sentry from '@sentry/browser';

const RETELL_HEIGHT_VAR = '--rtl-h';
const DEFAULT_MAX_NO_SUBMIT_TIME = 60000;
const DEFAULT_MINIMUM_SUBMIT_WAIT_TIME = 1000;
const CONFIGURED_GRADE_TIMERS = {
  K: {
    MAX_NO_SUBMIT_TIME: DEFAULT_MAX_NO_SUBMIT_TIME,
    MINIMUM_SUBMIT_WAIT_TIME: DEFAULT_MINIMUM_SUBMIT_WAIT_TIME,
  },
  1: {
    MAX_NO_SUBMIT_TIME: DEFAULT_MAX_NO_SUBMIT_TIME,
    MINIMUM_SUBMIT_WAIT_TIME: DEFAULT_MINIMUM_SUBMIT_WAIT_TIME,
  },
  2: {
    MAX_NO_SUBMIT_TIME: DEFAULT_MAX_NO_SUBMIT_TIME,
    MINIMUM_SUBMIT_WAIT_TIME: DEFAULT_MINIMUM_SUBMIT_WAIT_TIME,
  },
  3: {
    MAX_NO_SUBMIT_TIME: DEFAULT_MAX_NO_SUBMIT_TIME,
    MINIMUM_SUBMIT_WAIT_TIME: DEFAULT_MINIMUM_SUBMIT_WAIT_TIME,
  },
  4: {
    MAX_NO_SUBMIT_TIME: DEFAULT_MAX_NO_SUBMIT_TIME,
    MINIMUM_SUBMIT_WAIT_TIME: DEFAULT_MINIMUM_SUBMIT_WAIT_TIME,
  },
  5: {
    MAX_NO_SUBMIT_TIME: DEFAULT_MAX_NO_SUBMIT_TIME,
    MINIMUM_SUBMIT_WAIT_TIME: DEFAULT_MINIMUM_SUBMIT_WAIT_TIME,
  },
  6: {
    MAX_NO_SUBMIT_TIME: DEFAULT_MAX_NO_SUBMIT_TIME,
    MINIMUM_SUBMIT_WAIT_TIME: DEFAULT_MINIMUM_SUBMIT_WAIT_TIME,
  },
}


const Retell = ({
	isInstruction,
	videoURL,
	speech,
	onDone,
	storyId,
	activityId,
	phraseIndex,
	setRecordingProps,
	shouldUseAOS,
	setHideParentRepeatButton,
	setHideParentListeningStatus = () => {},
}) => {
  // State
  const [submitButtonEnabled, setSubmitButtonEnabled] = useState(false);
  const [repeatButtonEnabled, setRepeatButtonEnabled] = useState(false);
  const [showCheckmarkOverlay, setShowCheckmarkOverlay] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [submitTimerElapsed, setSubmitTimerElapsed] = useState(0);
  const [showDialog, setShowDialog] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
	const [hasSpokenAfterVideoPrompt, setHasSpokenAfterVideoPrompt] = useState(false);
	const [videoButtonsEnabled, setVideoButtonsEnabled] = useState(false);
  const submitTimerIntervalRef = useRef(null);
  const containerRef = useRef(null);
  useContainerHeightVar(containerRef, RETELL_HEIGHT_VAR);

  const RETELL_COPY = getCopyByGradeband(COPY_STRINGS, 'retell');

  useEffect(() => {
		speech.stopListening();
    setHideParentRepeatButton(true);
    setHideParentListeningStatus(true);

    return () => {
      if (submitTimerIntervalRef.current) clearInterval(submitTimerIntervalRef.current);
      setHideParentRepeatButton(false);
      setHideParentListeningStatus(false);
    };
  }, []);

  useEffect(() => {
    if (!isInstruction) {
      speakIntroduction();
    }
  }, [isInstruction]);

  const getEnableSubmitTimeout = () => {
    const grade = getUserData().sessionData.grade;
    return CONFIGURED_GRADE_TIMERS[grade]?.MINIMUM_SUBMIT_WAIT_TIME || DEFAULT_MINIMUM_SUBMIT_WAIT_TIME;
  };

  const getMaxNoSubmitTimeout = () => {
    const grade = getUserData().sessionData.grade;
    return CONFIGURED_GRADE_TIMERS[grade]?.MAX_NO_SUBMIT_TIME || DEFAULT_MAX_NO_SUBMIT_TIME;
  };

  const speakIntroduction = () => {
    setShowVideo(true);
    speech.speakWithPromise({
      textToSpeak: getCopyByGradeband(RETELL_COPY, 'introduction'),
      useGenerative: true
    }).finally(() => {
      setVideoPlaying(true);
    });
  };

	const speakAfterVideoPrompt = () => {
		if (hasSpokenAfterVideoPrompt) return;

		setHasSpokenAfterVideoPrompt(true);
		speech.speakWithPromise({
			textToSpeak: getCopyByGradeband(RETELL_COPY, 'after_video_prompt'),
			useGenerative: true
		}).finally(() => {
			setVideoButtonsEnabled(true);
		});
	}

  const speakDialogInstructions = () => {
		speech.abortSpeech(() => {
			setShowVideo(false);
			setShowDialog(true);
			speech.speakWithPromise({
				textToSpeak: getCopyByGradeband(RETELL_COPY, "dialog_instructions"),
				useGenerative: true
			}).finally(() => {
				setIsListening(true);
				setRepeatButtonEnabled(true);
				
				const recordingProps = {
					activityId: activityId,
					storyId: storyId,
					phraseIndex: phraseIndex,
					activityType: 'retell',
					studentId: getUserData().userId,
					clearHistory: true
				};
				
				setRecordingProps(recordingProps);
				speech.setForceAOS(shouldUseAOS, () => {
					speech.startListening(null, null, false, false, null, false, false);
					startSubmitTimer();
				});
			});
		});
  };

  const startSubmitTimer = () => {
    if (submitTimerIntervalRef.current) clearInterval(submitTimerIntervalRef.current);

    setSubmitTimerElapsed(0);
    const enableSubmitTimeout = getEnableSubmitTimeout();
    const maxNoSubmitTimeout = getMaxNoSubmitTimeout();

    submitTimerIntervalRef.current = setInterval(() => {
      let prevElapsedTime;
      let newElapsedTime;

      setSubmitTimerElapsed(prev => {
        prevElapsedTime = prev;
        newElapsedTime = prevElapsedTime + 250;
        return newElapsedTime;
      });

      if (prevElapsedTime <= enableSubmitTimeout && newElapsedTime >= enableSubmitTimeout) {
        setSubmitButtonEnabled(true);
      } else if (newElapsedTime > maxNoSubmitTimeout) {
        setSubmitTimerElapsed(maxNoSubmitTimeout);
        handleSubmit();
      }
    }, 250);
  }

  const handleSubmit = () => {
    if (submitTimerIntervalRef.current) clearInterval(submitTimerIntervalRef.current);
    setIsListening(false);
    setSubmitButtonEnabled(false);
    setRepeatButtonEnabled(false);
    speech.stopListening();
    
    speech.speakWithPromise({
      textToSpeak: getCopyByGradeband(RETELL_COPY, "submit_confirmation"),
      useGenerative: true
    }).finally(() => {
      setShowCheckmarkOverlay(true);
      if (onDone) onDone();
    });
  };

  // Render
  return (
    <div className="retellScreen" ref={containerRef}>
      {showVideo && (
        <VideoPlayer
          videoURL={videoURL}
          text={"Retell Video"}
          unmountWhenDone={getUserData().sessionData.isiPad}
          key="theOnlyRetellVideo"
          speech={speech}
          assessment={true}
          videoPlaying={videoPlaying}
          // Show the overlay to continue after the video is done
          allowReplay={true}
          maxReplays={5}
          onDone={() => {
            setVideoPlaying(false);
						speakAfterVideoPrompt();
          }}
          onNext={videoButtonsEnabled ? () => {
            speakDialogInstructions();
          } : () => {}}
          useButtonLabels={true}
					replayDisabled={!videoButtonsEnabled}
        />
      )}
      {showDialog && (
        <DialogDisplay
          repeatButtonEnabled={repeatButtonEnabled}
          showRepeatButton={true}
          showMicrophone={true}
          showPauseOverlay={!isListening}
          showCheckmarkOverlay={showCheckmarkOverlay}
          showTimer={true}
          showStudentTurn={true}
          showPrompt={true}
          onClickRepeat={() => {
            clearInterval(submitTimerIntervalRef.current);
            setSubmitTimerElapsed(0);
            setSubmitButtonEnabled(false);
            setRepeatButtonEnabled(false);
            setIsListening(false);
            speakDialogInstructions();
          }}
          fakeActiveListeningStatus={isListening}
          transcript={speech.transcript}
          submitTimerElapsed={submitTimerElapsed}
          getNoSubmitTimeout={getMaxNoSubmitTimeout}
          promptText={getCopyByGradeband(RETELL_COPY, "what_happened_prompt")}
          assessment={true}
          submitButton={
            <RetellSubmitButton submitEnabled={submitButtonEnabled} onSubmit={handleSubmit} />
          }
        />
      )}
    </div>
  );
};

export default Retell;
